import { getApiUrl } from "./consts";

/**
 * Converts an array to a comma-separated parameter string
 * @param {string[]} array
 * @returns Parameter string
 */
const arrayToParameter = (array) => array.reduce((acc, cur) => acc + "," + cur);

/**
 * Fetches the channel data rows from the API
 * @param {string} accessToken Access token from Auth0
 * @param {string[]} [fields] Fields to include in response
 * @returns Channel data rows
 */
export async function fetchChannelDataRows(accessToken, fields) {
    // do not pull data from cache if url has search param "noCachedRows"
    const noCachedRows = new URLSearchParams(window.location.search).get("noCachedRows") === "";

    // return cached rows if they exist
    const { rows: cachedRows, fetchedFields: cachedFetchedFields } = !noCachedRows ? getCachedRows() : { rows: [], fetchedFields: [] };
    const fetchedFieldsSet = new Set(cachedFetchedFields);
    const neededFields = fields.filter((field) => !fetchedFieldsSet.has(field));

    // fetch rows from API
    const apiUrl = getApiUrl();
    let url = `${apiUrl}/channel`;
    if (neededFields.length > 0) url += `?fields=${arrayToParameter(["channel_id", ...neededFields])}`;
    else return cachedRows;

    const res = await fetch(url, {
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    });
    const data = await res.json();
    if (res.status !== 200) {
        throw new Error(`${res.status}: ${data.message}`)
    }

    // combine fetched data with cached data
    const cachedRowsMap = new Map();
    cachedRows.forEach((row) => cachedRowsMap.set(row.channel_id, row));
    const combinedData = data.map((row) => {
        return {...row, ...cachedRowsMap.get(row.channel_id)}
    });
    setCachedRows(combinedData, [...cachedFetchedFields, ...neededFields]);
    return combinedData;
}

/**
 * Clears the cached rows from session storage
 */
export function clearRowCache() {
    sessionStorage.removeItem("rows");
}

/**
 * @typedef CachedRows
 * @type {object}
 * @property {object[]} rows
 * @property {string[]} fetchedFields
 */
/**
 * Gets the channel data rows from session storage
 * @returns {CachedRows} Channel data rows that have been cached, or null
 */
function getCachedRows() {
    return JSON.parse(sessionStorage.getItem("rows")) || { rows: [], fetchedFields: [] };
}


/**
 * Sets the channel data row cache
 * @param {object[]} rows Channel data rows
 * @param {string[]} fetchedFields Fields that have been fetched
 */
function setCachedRows(rows, fetchedFields) {
    sessionStorage.setItem("rows", JSON.stringify({ rows, fetchedFields }));
}


/**
 * Fetches the channel data for a specific channel from the API
 * @param {number} channelID The row ID for the channel
 * @param {string} accessToken Access token from Auth0
 * @param {string[]} [fields] Fields to include in response
 * @returns Channel data
 */
export async function fetchChannelData(channelID, accessToken, fields) {
    const apiUrl = getApiUrl();
    let url = `${apiUrl}/channel/${channelID}`;
    if (fields) url += `?fields=${arrayToParameter(fields)}`;
    const res = await fetch(url, {
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    });
    const data = await res.json();
    if (res.status !== 200) {
        throw new Error(`${res.status}: ${data.message}`)
    }
    return data;
}


export async function editChannelData(channelID, accessToken, updates){
    updates = JSON.stringify(updates)
    const apiUrl = getApiUrl();
    let url = `${apiUrl}/channel/${channelID}`;
    await fetch(url, {
        method: 'PATCH',
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
        body: updates,
    })
}


export async function fetchSchema(accessToken){

    let cachedSchema = getCachedSchema()
    if (cachedSchema) return cachedSchema

    const apiUrl = getApiUrl();
    let url = `${apiUrl}/schema/channel`;
    const res = await fetch(url, {
        method: 'GET',
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    })

    const schema = await res.json();
    if (res.status !== 200) {
        throw new Error(`${res.status}: ${schema.message}`)
    }

    setCachedSchema(schema)
    return schema;
}


function getCachedSchema() {
    return JSON.parse(sessionStorage.getItem("schema"));
}


function setCachedSchema(schema) {
    sessionStorage.setItem("schema", JSON.stringify(schema));
}

export async function getLiveStatus(accessToken, channelID){

    const apiUrl = getApiUrl();
    let url = `${apiUrl}/channel/${channelID}/status`;
        const res = await fetch(url, {
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    });
    const data = await res.json();
    if (res.status !== 200) {
        throw new Error(`${res.status}: ${data.message}`)
    }
    return data;
}

export function getNextLive(){
    return "5pm PST";
}

